import { getQuery, postMutation, } from "src/components/ApolloConnection/ApolloProviderComponent";
import { GET_CLINIC_UPDATE_REQUEST, LIST_CLINIC_UPDATE_REQUESTS, LIST_CLINICS } from "src/components/graphql/GraphQLquery";
import { ACTIVATE_CLINIC, ADD_CLINIC_BANK_DETAILS, DEACTIVATE_CLINIC, REVIEW_CLINIC_UPDATE_REQUEST, UPDATE_CLINIC, } from "src/components/graphql/GraphQLmutation";
import { snackbar } from "@mobiscroll/react5";

export async function listClinics(take) {
  try {
    const response = await getQuery(LIST_CLINICS(500));
    if (typeof response == "undefined" || response?.errors) {
      throw new Error("error", response);
    } else {
      snackbar({ message: "All clinics fetched successfully.", color: "success", });
      if (response?.errors) {
        throw new Error("error", response);
      } else if (response?.data?.listAllClinics?.data) {
        return response?.data?.listAllClinics;
      } else {
        throw new Error("error", response);
      }
    }
  } catch (e) {
    snackbar({ message: "Error at fetching clinics. Try again", color: "danger", });
    console.error("Error at fetching clinics ", e);
    return { data: [] };
  }
}

export async function activateClinic(data) {
  try {
    const response = await postMutation(ACTIVATE_CLINIC, data);

    if (!response || response.errors) {
      snackbar({ message: "Error at activating clinic: ", color: "danger", });
      return null;
    }

    const result = response?.data?.activateClinic;

    if (!(result?.statusCode >= 200 && result?.statusCode < 300) || result?.errors) {
      snackbar({ message: "Error at activating clinic: ", color: "danger", });
      return null;
    }

    snackbar({ message: "Clinic Activated successfully.", color: "success", });
    return true;

  } catch (e) {
    snackbar({ message: "Error at activating clinic: ", color: "danger", });
    console.error("Error at activating clinic: ", e);
    return false;
  }
}

export async function deactivateClinic(data) {
  try {
    const response = await postMutation(DEACTIVATE_CLINIC, data);
    if (typeof response == "undefined" || response.errors) {
      throw new Error("error", response);
    } else {
      if (
        !(
          response?.data?.deactivateClinic?.statusCode >= 200 &&
          response?.data?.deactivateClinic?.statusCode < 300
        ) ||
        response?.data?.deactivateClinic?.errors
      ) {
        throw new Error("error", response?.data?.deactivateClinic.errors);
      }
      snackbar({ message: "Clinic Deactivated successfully.", color: "success", });
      return true;
    }
  } catch (e) {
    snackbar({ message: "Unable to deactivate clinic. Please try again later.", color: "danger", });
    console.error("Error at Clinic List: ", e);
    return false;
  }
}

// Fields accepted by the AppSync `UpdateClinicRequest` input. Anything outside
// this set (e.g. `name`, `clinicId`) makes AppSync reject the whole mutation with
// "field that is not defined for input object type 'UpdateClinicRequest'".
const UPDATE_CLINIC_ALLOWED_FIELDS = [
  "id",
  "logoLink",
  "address",
  "signature",
  "description",
  "emergencyNumber",
  "helplineNumber",
  "about",
  "specialities",
  "medicalRegistrationNumber",
  "upiId",
  "subscriptionType",
  "planId",
  "freeUntil",
  "contractAccepted",
  "clinicTermsAndConditionsforAppGeneric",
  "clinicLogoHeader",
];

export async function updateClinic(data) {
  try {
    // The clinic id always travels as `id` in UpdateClinicRequest (the backend
    // resolves clinic-admins by cognito group and ignores it). `clinicId` is not
    // a valid input field, so never forward it.
    const { clinicId, ...actualChanges } = data;

    const mutationPayload = { id: clinicId };
    for (const key of UPDATE_CLINIC_ALLOWED_FIELDS) {
      if (key !== "id" && actualChanges[key] !== undefined) {
        mutationPayload[key] = actualChanges[key];
      }
    }

    // `freeUntil` is an Int in the schema; the form supplies it as a string.
    if (mutationPayload.freeUntil !== undefined) {
      const days = parseInt(mutationPayload.freeUntil, 10);
      if (Number.isNaN(days)) {
        delete mutationPayload.freeUntil;
      } else {
        mutationPayload.freeUntil = days;
      }
    }

    const userRole = sessionStorage.getItem("userRole");
    const response = await postMutation(UPDATE_CLINIC, mutationPayload);

    // ... (rest of your error handling logic is fine)
    if (!response || response.errors) throw new Error("Network error");

    const result = response?.data?.updateClinic;
    if (!result) throw new Error("No response from server");

    if (result.statusCode >= 200 && result.statusCode < 300) {
      return {
        success: true,
        message: result?.message || ["SubAdmin", "AccountManager", "SuperAdmin"].includes(userRole)
          ? "Change request submitted"
          : "Clinic updated successfully"
      };
    }

    throw new Error(result.message || "Failed to update");

  } catch (e) {
    snackbar({ message: e.message, color: "danger" });
    return { success: false, message: e.message };
  }
}

const CLINIC_UPDATE_REQUESTS_TAKE = 100;

export async function listClinicUpdateRequests() {
  try {
    const response = await getQuery(
      LIST_CLINIC_UPDATE_REQUESTS,
      { take: CLINIC_UPDATE_REQUESTS_TAKE, skip: 0, orderBy: "requestedAt=-1" },
      true,
    );

    if (response?.errors || !response?.data?.listClinicUpdateRequests) {
      throw new Error(response?.errors?.[0]?.message || "Failed to fetch clinic update requests");
    }

    const result = response?.data?.listClinicUpdateRequests;

    return {
      items: result?.data || [],
      totalCount: result?.totalCount ?? result?.data?.length ?? 0,
    };
  } catch (e) {
    snackbar({
      message: e.message || "Error fetching clinic update requests",
      color: "danger",
    });
    console.error("Error fetching clinic update requests:", e);
    return { items: [], totalCount: 0 };
  }
}

export async function getClinicUpdateRequest(requestId) {
  try {
    // Call GraphQL query
    const response = await getQuery(
      GET_CLINIC_UPDATE_REQUEST,
      { requestId: requestId },
    );

    // Check for GraphQL errors
    if (response?.errors || !response?.data?.getClinicUpdateRequest) {
      throw new Error("Failed to fetch clinic update request");
    }

    const result = response?.data?.getClinicUpdateRequest;

    // Check backend status code
    if (!(result?.statusCode >= 200 && result?.statusCode < 300)) {
      throw new Error(result?.message || "Failed to fetch clinic update request");
    }

    // Return request data
    return result?.data;

  } catch (e) {
    snackbar({
      message: e.message || "Error fetching clinic update request",
      color: "danger",
    });
    console.error("Error fetching clinic update request:", e);
    throw e;  // Re-throw so component can handle it
  }
}


export async function reviewClinicUpdateRequest(data) {
  try {
    // Call GraphQL mutation
    const response = await postMutation(REVIEW_CLINIC_UPDATE_REQUEST, data);

    // Check for GraphQL errors
    if (response?.errors || !response?.data?.reviewClinicUpdateRequest) {
      throw new Error("Failed to review clinic update request");
    }

    const result = response?.data?.reviewClinicUpdateRequest;

    // Check backend status code
    if (!(result?.statusCode >= 200 && result?.statusCode < 300)) {
      // Handle specific error: 409 = conflict (already reviewed or clinic changed)
      if (result?.statusCode === 409) {
        throw new Error(result?.message || "Request already reviewed or clinic changed since submission");
      }
      throw new Error(result?.message || "Failed to review clinic update request");
    }

    // Show success message
    const actionName = data.action === "Approve" ? "Approved" : "Rejected";
    snackbar({
      message: `Request ${actionName} successfully`,
      color: "success",
    });

    return true;

  } catch (e) {
    snackbar({
      message: e.message || "Error reviewing clinic update request",
      color: "danger",
    });
    console.error("Error reviewing clinic update request:", e);
    return false;
  }
}

export async function addClinicBankDetails(data) {
  try {
    const response = await postMutation(ADD_CLINIC_BANK_DETAILS, data);

    if (response?.errors || !response?.data?.addClinicBankDetails) {
      throw new Error("Failed to add bank details");
    }

    const result = response.data.addClinicBankDetails;

    if (!(result?.statusCode >= 200 && result?.statusCode < 300)) {
      throw new Error(result?.message || "Failed to add bank details");
    }

    snackbar({
      message: result?.message || "Bank details submitted. Super Admin will review them under Bank Details.",
      color: "success",
    });
    return true;
  } catch (e) {
    snackbar({ message: e.message || "Error saving bank details", color: "danger" });
    console.error("Error at addClinicBankDetails:", e);
    return false;
  }
}